import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useUser } from "../lib/userAuth";
import { useSEO } from "../lib/useSEO";
import WaterfallCard from "../components/WaterfallCard";

export default function Saved() {
  const { user, loading, signIn } = useUser();
  const [tab, setTab] = useState("saved"); // saved | visited
  const [savedItems, setSavedItems] = useState([]);
  const [visitedItems, setVisitedItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useSEO({ title: "Saved & visited waterfalls | WaterfallFinder", description: "Your saved and visited waterfalls — bookmarks and trip log." });

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [b, v] = await Promise.all([
          api.get("/me/bookmarks"),
          api.get("/me/visits"),
        ]);
        setSavedItems(b.data.items || []);
        setVisitedItems(v.data.items || []);
      } finally { setLoaded(true); }
    })();
  }, [user]);

  if (loading) return <main className="pt-24 text-center text-stone-500">Loading…</main>;

  if (!user) {
    return (
      <main className="pt-24 max-w-2xl mx-auto px-4 text-center" data-testid="saved-signin">
        <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight text-stone-900 mb-4">Save your favourite waterfalls</h1>
        <p className="text-stone-600 mb-8">Sign in to bookmark waterfalls, mark places you've visited, and access everything on any device.</p>
        <button onClick={() => signIn("/saved", "Sign in to access your saved waterfalls and the list of places you've visited.")} className="btn-at btn-at-green" data-testid="saved-signin-btn">Sign in with Google</button>
      </main>
    );
  }

  const items = tab === "saved" ? savedItems : visitedItems;
  const emptyText = tab === "saved"
    ? "No saved waterfalls yet. Tap the ❤ on any card to save it here."
    : "No visited waterfalls yet. On any waterfall page tap “I've been here”.";

  return (
    <main className="pt-20 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10" data-testid="saved-page">
      <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-stone-900">Your waterfalls</h1>

      <div className="mt-6 mb-8 border-b border-stone-200 flex gap-6" data-testid="saved-tabs">
        <button
          onClick={() => setTab("saved")}
          data-testid="saved-tab-saved"
          className={`pb-3 text-sm font-semibold border-b-2 ${tab === "saved" ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-900"}`}
        >
          Want to visit ({savedItems.length})
        </button>
        <button
          onClick={() => setTab("visited")}
          data-testid="saved-tab-visited"
          className={`pb-3 text-sm font-semibold border-b-2 ${tab === "visited" ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-900"}`}
        >
          Visited ({visitedItems.length})
        </button>
      </div>

      {!loaded ? (
        <p className="text-stone-500">Loading…</p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-stone-200 p-10 text-center">
          <p className="text-stone-700 mb-4">{emptyText}</p>
          <Link to="/waterfalls" className="btn-at btn-at-green">Explore waterfalls</Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-5 gap-y-10">
          {items.map((w) => <WaterfallCard key={w.id} wf={w} />)}
        </div>
      )}
    </main>
  );
}
